package ui.report.campaign

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithCache
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.*
import androidx.compose.ui.graphics.drawscope.draw
import androidx.compose.ui.graphics.drawscope.drawIntoCanvas
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.drawText
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import api.CampaignType
import api.ResourceType
import com.netguru.multiplatform.charts.ChartAnimation
import com.netguru.multiplatform.charts.line.LineChart
import com.netguru.multiplatform.charts.line.LineChartColors
import com.netguru.multiplatform.charts.line.LineChartData
import com.netguru.multiplatform.charts.line.LineChartSeries
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.datetime.LocalTime
import org.ssoft.common.tools.EMPTY
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.*
import ui.widget.*
import kotlin.coroutines.resume
import kotlin.math.roundToInt
import kotlin.time.Duration.Companion.milliseconds

data class ReportCampaignArgs(
    var companyId: Long = -1,
    var campaignGroupId: Long = -1,
    var withBackNav: Boolean = false
)

/**
 * LED MREZA
 * EDBF3161
 * 12345
 */
@Composable
@ComposableNavNode(ReportCampaignArgs::class)
fun ReportCampaignUI() {
    Box {
        with(ReportCampaignScope.remember()) {
            when (val state = vm.state) {
                is DataState.Error -> ErrUI()
                is DataState.Idle,
                is DataState.Loading -> LoadingUI()

                is DataState.Result -> ContentWrapper { inlineBackButton, captureScope ->
                    Content(state.nnvl, inlineBackButton, captureScope)
                }
            }
        }
    }
}

@Composable
private fun ReportCampaignScope.ContentWrapper(body: @Composable (inlineBackButton: Boolean, captureScope: CaptureScope) -> Unit) {
    val captureScope = CaptureScope.remember()
    val stateVertical = rememberScrollState(0)
    val scope = rememberCoroutineScope()

    fun download() {
        vm.state.entry?.apply {
            scope.launch {
                FilePicker.uploadImage(
                    imageBitmap = captureScope.capture(),
                    name = "${name}_from_${start.date}_to_${end.date}"
                )
            }
        }
    }

    if (display.isMobile || display.resolution.width < 640) {
        Box(
            Modifier.fillMaxSize()
                .verticalScroll(stateVertical)
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(24.dp)) {
                body(true, captureScope)
                FloatingActionButton(
                    onClick = { download() },
                    modifier = Modifier.align(Alignment.CenterHorizontally)
                ) { Icon(Svg.Download, null) }
                Space(8.dp)
            }
        }
    } else {
        Box(Modifier.fillMaxSize()) {
            Box(
                Modifier.fillMaxSize()
                    .verticalScroll(stateVertical)
            ) {
                Card(
                    Modifier.width(512.dp)
                        .padding(vertical = 24.dp).align(Alignment.Center)
                ) {
                    body(false, captureScope)
                }

            }

            FloatingActionButton(
                onClick = { nav.up() },
                modifier = Modifier.padding(24.dp)
                    .align(Alignment.TopStart)
            ) { Icon(Svg.Arrow_left, null) }

            FloatingActionButton(
                onClick = { download() },
                modifier = Modifier.padding(24.dp)
                    .align(Alignment.BottomEnd)
            ) { Icon(Svg.Download, null) }
        }
    }
}

@Composable
fun Modifier.capture(scope: CaptureScope): Modifier {
    return if (scope.inProgress) this.then(drawWithCache {

        val width = this.size.width.toInt()
        val height = this.size.height.toInt()

        onDrawWithContent {
            val pic = ImageBitmap(width, height)

            val pictureCanvas = Canvas(pic)

            draw(this, this.layoutDirection, pictureCanvas, this.size) {
                this@onDrawWithContent.drawContent()
            }
            scope.onCapture(pic)
            drawIntoCanvas { canvas ->
                canvas.drawImage(pic, Offset.Zero, Paint())
            }
        }
    }) else this
}

class CaptureScope private constructor(
    val background: Color
) {

    var inProgress by mutableStateOf(false)
        private set

    companion object {
        @Composable
        fun remember(background: Color = MaterialTheme.colorScheme.surfaceContainer): CaptureScope {

            return remember {
                CaptureScope(background)
            }
        }
    }

    var onCapture: (ImageBitmap) -> Unit = {}

    suspend fun capture(): ImageBitmap {
        val pic = captureInternal()
        val template = ImageBitmap(pic.width, pic.height)
        Canvas(template).apply {
            drawRect(0F, 0F, pic.width.toFloat(), pic.height.toFloat(), Paint().apply {
                color = background
            })
            drawImage(pic, Offset.Zero, Paint().apply {
                isAntiAlias = true
                filterQuality = FilterQuality.High
            })
        }
        return template
    }

    private suspend fun captureInternal(): ImageBitmap = suspendCancellableCoroutine { continuation ->
        onCapture = { bitmap ->
            inProgress = false
            if (!continuation.isCompleted) {
                continuation.resume(bitmap)
            }
        }
        inProgress = true
    }

}

// TODO  https://stackoverflow.com/questions/74450838/convert-a-composable-view-into-image-in-jetpack-compose
@Composable
private fun ReportCampaignScope.Content(
    nnvl: ReportCampaignScope.Wrapper,
    inlineBackButton: Boolean,
    captureScope: CaptureScope
) {

    Column(
        Modifier.fillMaxWidth()
            .background(MaterialTheme.colorScheme.surfaceContainer)
            .capture(captureScope)
            .padding(vertical = 24.dp, horizontal = 16.dp)
    ) {
        Name(inlineBackButton, nnvl)
        Space(24.dp)
        Dates(nnvl)
        DividerHigh()
        State(nnvl)
        DividerHigh()
        Summary(nnvl)
        DividerHigh()
        ImpressionCounter(nnvl)
        if (nnvl.isMultipleDays) {
            DividerHigh()
            Chart(nnvl)
        }
        if (nnvl.resources.isNotEmpty()) {
            DividerHigh()
            Visuals(nnvl)
        }

        if (nnvl.devices.isNotEmpty()) {
            DividerHigh()
            Devices(nnvl)
        }
        if (nnvl.oh.isNotEmpty()) {
            DividerHigh()
            Locations(nnvl)
        }
    }
}

@Composable
private fun ChartDialog(
    label: String,
    state: DialogState,
    chart: ReportCampaignScope.Wrapper.Chart
) {

    val scope = rememberCoroutineScope()
    Dialog(
        state = state,
        background = Color.Black.copy(alpha = 0.8F),
        onDismiss = {
            scope.launch {
                chart.reset()
            }
        }
    ) {
        val rangeState = DialogState.remember()
        DateRangePickerDialog(
            state = rangeState,
            start = chart.limitStart,
            end = chart.limitEnd,
            enabledRange = chart.start to chart.end
        ) {
            scope.launch {
                chart.rebuild()
            }
        }
        Column(
            Modifier.fillMaxWidth()
                .align(Alignment.BottomCenter)
        ) {
            FloatingActionButton(
                containerColor = Color.White,
                contentColor = Color.DarkGray,
                onClick = { state.hide() },
                modifier = Modifier.align(Alignment.End)
                    .padding(24.dp)
            ) {
                Icon(Svg.Close, null)
            }

            Column(
                verticalArrangement = Arrangement.spacedBy(24.dp),
                modifier = Modifier.fillMaxWidth()
                    .clip(RoundedCornerShape(8, 8, 0, 0))
                    .background(MaterialTheme.colorScheme.onBackground)
                    .padding(24.dp)
            ) {
                Row {
                    Text(
                        text = label,
                        color = MaterialTheme.colorScheme.background,
                        style = MaterialTheme.typography.headlineLarge,
                        modifier = Modifier.weight(1F)
                            .align(Alignment.CenterVertically)
                    )
                    FloatingActionButton(
                        onClick = { rangeState.show() },
                        modifier = Modifier
                            .align(Alignment.CenterVertically)
                    ) {
                        Icon(Svg.Calendar, null)
                    }
                }
                MaterialTheme(colorScheme = lightColorScheme()) {
                    ChartView(chart, Modifier.height(300.dp))
                }
            }
        }
    }
}

@Composable
private fun ReportCampaignScope.Chart(nnvl: ReportCampaignScope.Wrapper) {
    val state = DialogState.remember()
    ChartDialog(
        label = Res.string.report_campaign__label_graph.rememberString(),
        state = state,
        chart = nnvl.chart
    )

    Card(
        onClick = { state.show() },
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.inverseSurface,
            contentColor = MaterialTheme.colorScheme.inverseOnSurface,
        )
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.fillMaxWidth()
                .padding(8.dp)
        ) {
            Icon(Svg.Chart_line, null)
            Text(
                text = Res.string.report_campaign__label_graph.rememberString(),
                modifier = Modifier.weight(1F)
            )
            Icon(Svg.Fit_to_screen_outline, null)
        }
    }
    Space(16.dp)

    MaterialTheme(colorScheme = darkColorScheme()) {
        ChartView(nnvl.chart, Modifier.height(300.dp))
    }
}


//@Composable
//private fun ChartView(nnvl: ReportCampaignScope.Wrapper, modifier: Modifier) {
//        LineChart(
//            modifier = modifier,
//            data = listOf(
//                Line(
//                    label = "",
//                    values = nnvl.impression.counter.map { it.toDouble() },
//                    color = SolidColor(Color(0xFF0FB9B1)),
//                    strokeAnimationSpec = tween(2000, easing = EaseInOutCubic),
//                    gradientAnimationDelay = 1000,
//                    drawStyle = DrawStyle.Stroke(),
//                )
//            ),
//            labelProperties = LabelProperties(
//                enabled = true,
//                labels = nnvl.labels,
//                rotation = LabelProperties.Rotation(
//                    mode = if( nnvl.labels.size < 10) Mode.IfNecessary else Mode.Force,
//                    degree = -90F)
//            ),
//        )
//
//}
@Composable
private fun ChartView(chart: ReportCampaignScope.Wrapper.Chart, modifier: Modifier) {
    Crossfade(
        targetState = chart.data,
        modifier = modifier.fillMaxWidth()
    ) {
        if (it.isNotEmpty()) {
            LineChart(
                lineChartData = LineChartData(
                    listOf(
                        LineChartSeries(
                            lineWidth = 1.dp,
                            dashedLine = false,
                            dataName = "",
                            lineColor = MaterialTheme.colorScheme.onBackground,
                            listOfPoints = it
                        )
                    )
                ),
                colors = LineChartColors(
                    grid = Color.Transparent,
                    surface = MaterialTheme.colorScheme.onBackground,
                    overlayLine = MaterialTheme.colorScheme.onSurface,
                ),
                modifier = Modifier.fillMaxSize(),
                xAxisLabel = chart.xAxisLabel,
                yAxisLabel = {
                    Text(
                        fontSize = 12.sp,
                        color = MaterialTheme.colorScheme.onBackground,
                        text = (it as Number).toInt().toString(),
                        textAlign = TextAlign.Center
                    )
                },
                overlayDataEntryLabel = { _, value ->
                    Text(
                        text = (value as Number).toInt().toString(),
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.background,
                    )
                },
                overlayHeaderLabel = chart.overlayHeaderLabel,
                animation = ChartAnimation.Sequenced()
            )
        }
    }
}

@Composable
private fun ReportCampaignScope.Visuals(nnvl: ReportCampaignScope.Wrapper) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(horizontal = 8.dp)
    ) {
        Icon(Svg.Multimedia, null)
        Text(Res.string.report_campaign__label_visuals.rememberString())
    }
    Space(16.dp)
    nnvl.resources.forEachIndexed { index, item ->
        if (index != 0) Space(8.dp)
        Card {
            Row(Modifier.fillMaxWidth()) {
                LazyImage(
                    id = item.id,
                    modifier = Modifier.width(256.dp)
                        .aspectRatio(item.width.toFloat() / item.height.toFloat())
                )
                Column(
                    modifier = Modifier.padding(8.dp)
                        .align(Alignment.CenterVertically),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        Icon(Svg.Multimedia, null)
                        Text(
                            text = ResourceType[item.type].labelRes.rememberString(),
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        Icon(Svg.MoveResize, null)
                        Text(
                            text = "${item.width} x ${item.height}",
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        Icon(Svg.Clock_outline, null)
                        Text(
                            text = item.duration.milliseconds.toString(),
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                    }
                    Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        Icon(Svg.Animation_play, null)
                        Text(
                            text = "${nnvl.impressionByResource[item.id] ?: 0}x",
                            modifier = Modifier.align(Alignment.CenterVertically)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ReportCampaignScope.Devices(nnvl: ReportCampaignScope.Wrapper) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(horizontal = 8.dp)
    ) {
        Icon(Svg.Device, null)
        Text(Res.string.report_campaign__label_devices.rememberString())
    }
    Space(16.dp)
    nnvl.devicesByLocation.forEachIndexed { groupIndex, locationData ->
        if (groupIndex != 0) Space(8.dp)

        Row(
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier.padding(horizontal = 8.dp)
        ) {
            Canvas(Modifier.size(4.dp).align(Alignment.CenterVertically)) {
                drawCircle(Color.White, size.width / 2F)
            }
            Text(
                text = locationData.first,
                style = MaterialTheme.typography.labelMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.fillMaxWidth()
                    .weight(1F)
            )
        }
        Space(4.dp)
        locationData.second.forEachIndexed { i, device ->
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.fillMaxWidth()
                    .padding(start = 16.dp),
            ) {
                Text(
                    text = device.name,
                    modifier = Modifier.weight(1F),
                    overflow = TextOverflow.Ellipsis,
                    maxLines = 1
                )
                Text(
                    text = "${nnvl.impressionByDevice[device.id] ?: 0}x",
                    textAlign = TextAlign.End
                )
            }
            if (i < locationData.second.lastIndex) {
                HorizontalDivider(
                    modifier = Modifier.padding(start = 16.dp)
                        .padding(vertical = 2.dp)
                )
            }
        }
    }
}

@Composable
private fun ReportCampaignScope.Locations(nnvl: ReportCampaignScope.Wrapper) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(horizontal = 8.dp)
    ) {
        Icon(Svg.Store, null)
        Text(Res.string.report_campaign__label_location.rememberString())
    }
    Space(16.dp)
    nnvl.oh.forEachIndexed { groupIndex, locationData ->
        if (groupIndex != 0) Space(8.dp)

        Row(
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier.padding(horizontal = 8.dp)
        ) {
            Canvas(Modifier.size(4.dp).align(Alignment.CenterVertically)) {
                drawCircle(Color.White, size.width / 2F)
            }
            Text(
                text = locationData.name,
                style = MaterialTheme.typography.labelMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.fillMaxWidth()
                    .weight(1F)
            )


        }
        Space(4.dp)
        locationData.stack.forEachIndexed { i, ohPair ->
            Row(
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                modifier = Modifier.fillMaxWidth()
                    .padding(start = 16.dp),
            ) {
                Text(
                    text = ohPair.first,
                    modifier = Modifier.weight(1F)
                )
                Text(
                    text = ohPair.second,
                    textAlign = TextAlign.End
                )
            }
            if (i < locationData.stack.lastIndex) {
                HorizontalDivider(
                    modifier = Modifier.padding(start = 16.dp)
                        .padding(vertical = 2.dp)
                )
            }
        }
    }
}

@Composable
private fun ReportCampaignScope.Summary(nnvl: ReportCampaignScope.Wrapper) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(horizontal = 8.dp)
    ) {
        Icon(Svg.Information_slab_circle_outline, null)
        Text(Res.string.report_campaign__label_summary.rememberString())
    }
    Space(16.dp)

    when (nnvl.type) {
        CampaignType.NONE -> Unit
        CampaignType.IMPRESSION -> {
            TwoFieldText(
                label = Res.string.report_campaign__label_visual_duration.rememberString(),
                message = nnvl.primary.settings.duration.milliseconds,
                modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
            )
            TwoFieldText(
                label = Res.string.report_campaign__label_visual_repeats.rememberString(),
                message = "${nnvl.primary.settings.count}x",
                modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
            )
            TwoFieldText(
                label = Res.string.report_campaign__label_visual_media_space.rememberString(),
                message = "${nnvl.medialSpace}%",
                modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
            )
        }

        CampaignType.SLOT -> {
            TwoFieldText(
                label = Res.string.report_campaign__label_visual_duration.rememberString(),
                message = (nnvl.primary.settings.duration * nnvl.primary.settings.count).milliseconds,
                modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
            )
            TwoFieldText(
                label = Res.string.report_campaign__label_visual_slots.rememberString(),
                message = "${nnvl.primary.settings.count}",
                modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
            )
        }
    }
    DividerLow(Modifier.padding(start = 42.dp))

    TwoFieldText(
        label = Res.string.report_campaign__label_used_devices.rememberString(),
        message = "${nnvl.devices.size}x",
        modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
    )
    TwoFieldText(
        label = Res.string.report_campaign__label_used_visuals.rememberString(),
        message = "${nnvl.resources.size}x",
        modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
    )

    DividerLow(Modifier.padding(start = 42.dp))

    TwoFieldText(
        label = Res.string.report_campaign__label_impression_expected.rememberString(),
        message = "${nnvl.impressionsExpect}x",
        modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
    )
    TwoFieldText(
        label = Res.string.report_campaign__label_impression_max.rememberString(),
        message = "${nnvl.impressionsMax}x",
        modifier = Modifier.fillMaxWidth().padding(start = 42.dp)
    )
}

@Composable
private fun ReportCampaignScope.ImpressionCounter(nnvl: ReportCampaignScope.Wrapper) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(horizontal = 8.dp)
    ) {
        Icon(Svg.Calculator_variant_outline, null)
        Text(Res.string.report_campaign__label_impression.rememberString())
    }
    Space(16.dp)

    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(start = 42.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(Res.string.report_campaign__label_impression_all.rememberString())
            Text(Res.string.report_campaign__label_impression_com.rememberString())
            if (nnvl.primary.isNonCommercial) {
                Text(Res.string.report_campaign__label_impression_ncom.rememberString())
            }
        }
        Column(
            verticalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier.weight(1F)
        ) {
            Text(
                text = nnvl.impressionAll.percentage.all,
                modifier = Modifier.align(Alignment.End)
            )
            Text(
                text = nnvl.impressionAll.percentage.com,
                modifier = Modifier.align(Alignment.End)
            )
            if (nnvl.primary.isNonCommercial) {
                Text(
                    text = nnvl.impressionAll.percentage.ncom,
                    modifier = Modifier.align(Alignment.End)
                )
            }
        }
        Column(
            verticalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier.weight(1F)
        ) {
            Text(
                text = nnvl.impressionAll.counter.all,
                modifier = Modifier.align(Alignment.End)
            )
            Text(
                text = nnvl.impressionAll.counter.com,
                modifier = Modifier.align(Alignment.End)
            )
            if (nnvl.primary.isNonCommercial) {
                Text(
                    text = nnvl.impressionAll.counter.ncom,
                    modifier = Modifier.align(Alignment.End)
                )
            }
        }
    }
}

@Composable
private fun ReportCampaignScope.State(nnvl: ReportCampaignScope.Wrapper) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(horizontal = 8.dp)
    ) {
        Icon(Svg.Movie_open_play_outline, null)
        Text(
            nnvl.stateString.rememberString(),
            fontWeight = FontWeight.Bold
        )
    }
}

@Composable
private fun ReportCampaignScope.Dates(nnvl: ReportCampaignScope.Wrapper) {
    Row(
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        modifier = Modifier.padding(horizontal = 8.dp)
    ) {
        Icon(Svg.Calendar, null)
        if (nnvl.start.time == LocalTime.EMPTY) {
            Text(nnvl.start.date.niceFormat())
        } else {
            Text(nnvl.start.niceFormat())
        }
        Icon(
            Svg.Arrow_left, null,
            modifier = Modifier.rotate(180F)
        )
        if (nnvl.end.time == LocalTime.EMPTY || nnvl.end.time.hour == 23) {
            Text(nnvl.end.date.niceFormat())
        } else {
            Text(nnvl.end.niceFormat())
        }
    }
    Space(16.dp)
    DateProgresIndicator(
        progress = nnvl.impressionAll.progress,
        modifier = Modifier.fillMaxWidth()
            .height(32.dp)
            .padding(horizontal = 8.dp)
    )
//    LinearProgressIndicator(
//        progress = { nnvl.impressionAll.progress },
//        modifier = Modifier.fillMaxWidth()
//            .padding(horizontal = 8.dp)
//    )
}

@Composable
private fun DateProgresIndicator(
    progress: Float,
    modifier: Modifier,
    bg: Color = MaterialTheme.colorScheme.surfaceVariant,
    fg: Color = MaterialTheme.colorScheme.inverseSurface
) {
    val textMeasurer = rememberTextMeasurer()

    val text = remember(progress) {
        "${(progress * 100F).roundToInt()}%"
    }

    val density = LocalDensity.current

    Canvas(modifier) {
        val divider = size.width * progress
        val y = size.height / 2F
        val strokeWidth = size.height / 4F
        val circleStart = when {
            (divider) > (size.width - size.height) -> size.width - size.height
            divider < 0F -> 0F
            else -> divider
        }

        val style = with(density) {
            TextStyle(
                fontSize = (size.height / 3F).toSp(),
                color = bg,
                fontWeight = FontWeight.Bold
            )
        }

        val textLayout = textMeasurer.measure(text, style)

        if (progress < 0.99) {
            drawLine(
                color = bg,
                start = Offset(
                    x = maxOf(size.width * 0.01F, divider),
                    y = y
                ),
                end = Offset(
                    x = size.width - strokeWidth,
                    y = y
                ),
                strokeWidth = strokeWidth,
                cap = StrokeCap.Round
            )
        }
        if (progress > 0.01) {
            drawLine(
                color = fg,
                start = Offset(
                    x = strokeWidth,
                    y = y
                ),
                end = Offset(
                    x = minOf(divider, size.width - strokeWidth),
                    y = y
                ),
                strokeWidth = strokeWidth,
                cap = StrokeCap.Round
            )
        }
        drawOval(
            color = fg,
            topLeft = Offset(
                x = circleStart,
                y = 0F
            ),
            size = Size(
                size.height,
                size.height
            )
        )
        drawText(
            textMeasurer = textMeasurer,
            text = text,
            style = style,
            topLeft = Offset(
                x = circleStart + (size.height / 2F) - (textLayout.size.width / 2F),
                y = y - (textLayout.size.height / 2F),
            )
        )
    }
}

@Composable
private fun ReportCampaignScope.Name(inlineBackButton: Boolean, nnvl: ReportCampaignScope.Wrapper) {
    Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
        if (inlineBackButton) {
            FloatingActionButton(
                onClick = { nav.up() }
            ) { Icon(Svg.Arrow_left, null) }
        }
        Text(
            text = nnvl.name,
            style = MaterialTheme.typography.displaySmall,
            modifier = Modifier.align(Alignment.CenterVertically)
        )
    }
}

@Composable
private fun DividerHigh(modifier: Modifier = Modifier) {
    HorizontalDivider(
        thickness = 1.dp,
        color = Color.Gray.copy(alpha = 0.6F),
        modifier = modifier.padding(vertical = 16.dp)
    )
}

@Composable
private fun DividerLow(modifier: Modifier = Modifier) {
    HorizontalDivider(
        thickness = 1.dp,
        color = Color.Gray.copy(alpha = 0.4F),
        modifier = modifier.padding(vertical = 8.dp)
    )
}
